import { computed, ref, Ref } from 'vue'
import { ActualStop, JobStopType } from '@ironsight/types'
import { useStore } from 'vuex-composition-helpers'
import Job from '@/models/job'
import { StopConfiguration } from '@/models/activity'
import { useGetJobStopsQuery } from '@/api/jobs'
import { sortBy } from 'lodash'

export function useJobStopForms(jobNumber: Ref<number>) {
  const store = useStore()
  const job = computed<Job>(() => store.getters['jobs/getById'](jobNumber.value))
  const { data: actualStops } = useGetJobStopsQuery(job.value.uuid)

  const getFormDefinitionById = computed(() => store.getters['forms/getById'])

  const formStopConfigurations = computed(() => {
    const formStopConfigurations =
      job.value?.activity?.formAssociations.map((fa) => ({
        [fa.formUuid]: fa.stopConfiguration,
      })) ?? []

    return Object.assign({}, ...formStopConfigurations)
  })

  const formsWithStopConfigurations = computed(
    () =>
      job.value?.activity?.formAssociations.map((form) =>
        Object.assign({}, form, {
          stopConfiguration: formStopConfigurations.value[form.formUuid],
        }),
      ) ?? [],
  )

  const allowedStopConfigurationsForStopType = (stopType: JobStopType) => {
    switch (stopType) {
      case JobStopType.PickUp:
        return [StopConfiguration.AfterEachStop, StopConfiguration.AfterEachPickUp]
      case JobStopType.DropOff:
        return [StopConfiguration.AfterEachStop, StopConfiguration.AfterEachDropOff]
    }
  }

  const getRequiredFormsForStopType = (stopType: JobStopType) => {
    const forms = formsWithStopConfigurations.value.filter((form) =>
      allowedStopConfigurationsForStopType(stopType).includes(form.stopConfiguration),
    )
    return sortBy(forms, [(form) => form.name.toLowerCase()])
  }

  const getFormsForStop = (stop: ActualStop) => {
    const requiredForms = getRequiredFormsForStopType(stop.stopType)

    const submittedForms = stop.formSubmissions.map((submission) => {
      const matchingAssociation = requiredForms.find(
        (form) => form.formUuid === submission.formUuid,
      )

      return matchingAssociation
        ? { ...matchingAssociation, ...submission, isFilledOut: true }
        : {
            ...submission,
            isFilledOut: true,
            name: getFormDefinitionById.value(submission.formUuid)?.name || 'Form',
          }
    })

    const missingRequiredForms = requiredForms
      .filter((form) => !stop.formSubmissions.some((s) => s.formUuid === form.formUuid))
      .map((form) => Object.assign({}, form, { isFilledOut: false }))

    return sortBy([...submittedForms, ...missingRequiredForms], [(form) => form.name.toLowerCase()])
  }

  const isAnyStopInProgress = computed(() => {
    return (
      actualStops.value?.some(
        (stop) => !stop.isSkipped && getFormsForStop(stop).some((form) => !form.isFilledOut),
      ) ?? false
    )
  })

  return {
    getRequiredFormsForStopType,
    getFormsForStop,
    isAnyStopInProgress,
  }
}
